import { RefObject, useCallback, useEffect, useLayoutEffect, useState } from 'react';

/** Ширины синхронизированы с `WinnerSelection2.scss` (`--ws2-axis`, `--ws2-col`). */
export const AXIS_WIDTH = 280;
export const AXIS_WIDTH_NARROW = 140;
export const PARTICIPANT_MIN_WIDTH = 260;
export const NARROW_BREAKPOINT = 700;

export const visibleParticipantCount = (width: number, total: number) => {
    const axis = width < NARROW_BREAKPOINT ? AXIS_WIDTH_NARROW : AXIS_WIDTH;
    const fit = Math.floor((width - axis) / PARTICIPANT_MIN_WIDTH);
    return Math.max(1, Math.min(total, fit));
};

interface IParticipantWindow {
    offset: number;
    size: number;
    canPrev: boolean;
    canNext: boolean;
    prev: () => void;
    next: () => void;
}

/**
 * Окно участников для pager: размер — сколько столбцов помещается в контейнер.
 * На узком экране всегда хотя бы один участник, остальное — горизонтальный scroll.
 */
export function useParticipantWindow(containerRef: RefObject<HTMLElement | null>, total: number): IParticipantWindow {
    const [width, setWidth] = useState(0);
    const [offset, setOffset] = useState(0);

    useLayoutEffect(() => {
        const element = containerRef.current;
        if (!element) return;
        setWidth(element.clientWidth);
        if (typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver((entries) => {
            const next = entries[0]?.contentRect.width;
            if (next != null) setWidth(next);
        });
        observer.observe(element);
        return () => observer.disconnect();
        // Матрица появляется после загрузки; при первом эффекте ref ещё пуст.
    }, [containerRef, total]);

    const size = width > 0 ? visibleParticipantCount(width, total) : Math.min(total, 3);
    const maxOffset = Math.max(0, total - size);

    useEffect(() => {
        setOffset((current) => Math.min(current, maxOffset));
    }, [maxOffset]);

    const prev = useCallback(() => setOffset((current) => Math.max(0, current - 1)), []);
    const next = useCallback(() => setOffset((current) => Math.min(maxOffset, current + 1)), [maxOffset]);
    // Окно сужается раньше, чем эффект поправит offset: считаем по ограниченному значению.
    const visibleOffset = Math.min(offset, maxOffset);

    return {
        offset: visibleOffset,
        size,
        canPrev: visibleOffset > 0,
        canNext: visibleOffset < maxOffset,
        prev,
        next,
    };
}
